import '@/config/tailwind-variants.config'
import '@/polyfills'
import '@/styles/main.css'

import { DefaultSeo } from 'next-seo'
import { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import localFont from 'next/font/local'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import * as amplitudeAnalytics from '@amplitude/analytics-browser'

import AmplitudeProvider from '@/contexts/AmplitudeProvider'
import { initializeGA, pageView } from '@/utils/analytics'
import DatadogInit from '@/utils/datadog-init'
import { getAssetURL } from '@/utils/getAssetURL'
import { getAndSaveSearchParams } from '@/utils/searchParams'
import pkg from '../../package.json'

const WhatsappButton = dynamic(() => import('@/components/WhatsappButton'))

const inter = localFont({
  src: [
    {
      path: '../assets/fonts/Inter-Variable.woff2',
      weight: '300 900',
      style: 'normal'
    }
  ],
  display: 'swap',
  variable: '--font-inter'
})

const myFont = localFont({
  src: [
    {
      path: '../assets/fonts/TonCondensedVF.woff2',
      weight: '100 900',
      style: 'normal'
    }
  ],
  display: 'swap',
  variable: '--ton-condensed'
})

const analyticsConfig: amplitudeAnalytics.Types.BrowserOptions = {
  autocapture: {
    pageViews: true,
    sessions: true
  },
  appVersion: pkg.version,
  identityStorage: 'cookie'
}

if (typeof window !== 'undefined') {
  amplitudeAnalytics.init(
    process.env.NEXT_PUBLIC_AMPLITUDE_ANALYTICS_API_KEY!,
    analyticsConfig
  )
}

export default function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()

  function handleBeforeHistoryChange() {
    sessionStorage?.setItem('amplitude-source', window.location.href)
  }

  useEffect(() => {
    initializeGA()
    getAndSaveSearchParams()
    pageView(window.location.href)

    router.events.on('beforeHistoryChange', handleBeforeHistoryChange)

    return () => {
      router.events.off('beforeHistoryChange', handleBeforeHistoryChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#00C700" />
        <meta name="url" content="https://www.ton.com.br/" />

        <meta
          name="google-site-verification"
          content="O1OQW32uto877Hm9dOMWb5q3AgEFyWaPU-jElYkYazs"
        />
      </Head>

      <DatadogInit />

      <DefaultSeo
        openGraph={{
          type: 'website',
          locale: 'pt_BR',
          url: 'https://ton.com.br/',
          site_name: 'Ton',
          images: [
            {
              url: getAssetURL('site-ton/OGImage.png'),
              width: 800,
              height: 600
            }
          ]
        }}
      />
      <AmplitudeProvider>
        <main className={`${inter.variable} ${myFont.variable} font-default`}>
          <div id="menuMobile" />
          <div id="modal" />
          <div id="drawer" />
          <div id="webchat" />
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA!}
            scriptProps={{
              async: false,
              defer: false,
              appendTo: 'head',
              nonce: undefined
            }}
          >
            <WhatsappButton />
            <Component {...pageProps} />
          </GoogleReCaptchaProvider>
        </main>
      </AmplitudeProvider>
    </>
  )
}
